import React, { useState, useEffect } from 'react';
import { MessageCircle, Save, Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

export default function AdminWhatsAppSettings() {
  const [settings, setSettings] = useState({
    enabled: false,
    phone_number: '',
    intro_message: 'Hi OnSiteHand support, I need help.',
    floating_button_enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/whatsapp-support`, { withCredentials: true });
        setSettings(res.data);
      } catch { /* use defaults */ }
      setLoaded(true);
    })();
  }, []);

  const handleSave = async () => {
    if (settings.enabled && !settings.phone_number.trim()) {
      toast.error('Phone number is required when WhatsApp support is enabled');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/whatsapp-support`, settings, { withCredentials: true });
      toast.success('WhatsApp support settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="card p-6" data-testid="whatsapp-settings">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-[#25D366]/10 rounded-lg">
          <MessageCircle className="h-5 w-5 text-[#25D366]" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">WhatsApp Support</h3>
          <p className="text-sm text-slate-500">Let users contact admin support via WhatsApp</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Enable toggle */}
        <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer" data-testid="wa-enabled-toggle">
          <div>
            <p className="text-sm font-medium text-slate-700">Enable WhatsApp Support</p>
            <p className="text-xs text-slate-500">Shows WhatsApp support buttons across the site</p>
          </div>
          <div className={`relative w-11 h-6 rounded-full transition-colors ${settings.enabled ? 'bg-[#25D366]' : 'bg-slate-300'}`}
            onClick={() => setSettings(s => ({ ...s, enabled: !s.enabled }))}>
            <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${settings.enabled ? 'translate-x-5' : ''}`} />
          </div>
        </label>

        {/* Phone number */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Support Phone Number</label>
          <input
            type="text"
            value={settings.phone_number}
            onChange={(e) => setSettings(s => ({ ...s, phone_number: e.target.value }))}
            placeholder="e.g. 07487567788 or 447487567788"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
            data-testid="wa-phone-input"
          />
          <p className="text-xs text-slate-400 mt-1">UK mobile (starting 07…) or international digits. We'll auto-convert UK numbers to wa.me format (strip leading 0, prepend 44).</p>
        </div>

        {/* Intro message */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Default Intro Message</label>
          <textarea
            value={settings.intro_message}
            onChange={(e) => setSettings(s => ({ ...s, intro_message: e.target.value }))}
            rows={2}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
            data-testid="wa-intro-input"
          />
          <p className="text-xs text-slate-400 mt-1">First line of the prefilled WhatsApp message</p>
        </div>

        {/* Floating button toggle */}
        <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer" data-testid="wa-floating-toggle">
          <div>
            <p className="text-sm font-medium text-slate-700">Floating Support Button</p>
            <p className="text-xs text-slate-500">Show persistent WhatsApp button in bottom-right corner</p>
          </div>
          <div className={`relative w-11 h-6 rounded-full transition-colors ${settings.floating_button_enabled ? 'bg-[#25D366]' : 'bg-slate-300'}`}
            onClick={() => setSettings(s => ({ ...s, floating_button_enabled: !s.floating_button_enabled }))}>
            <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${settings.floating_button_enabled ? 'translate-x-5' : ''}`} />
          </div>
        </label>

        {/* Preview */}
        {settings.enabled && settings.phone_number && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-xs font-medium text-green-800 mb-1">Preview: prefilled message for a logged-in employer</p>
            <pre className="text-xs text-green-700 whitespace-pre-wrap font-mono leading-relaxed">
{settings.intro_message}
{'\n'}Support Ref: USER_user_abc123_employer
Name: Jane Smith
Email: jane@company.co.uk
Role: employer
User ID: user_abc123
Company: Smith Builders Ltd
Page: Employer Dashboard
URL: https://onsitehand.co.uk/employer/dashboard
Environment: Production
{'\n'}Please help me with:</pre>
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
          data-testid="wa-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
